import React from 'react';

export type PlaceholderTemplateToken = {
  /** Kanonischer Name — so steht der Platzhalter in der gespeicherten Vorlage. */
  name: string;
  /** Beschriftung des Chips im Editor. */
  label: string;
};

type Props = {
  value: string;
  onChange: (next: string) => void;
  /** Bekannte Platzhalter; unbekannte Namen bleiben normaler Text. */
  resolveToken: (name: string) => PlaceholderTemplateToken | null;
  ariaLabelledBy: string;
  className?: string;
  multiline?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  onDragOver?: React.DragEventHandler<HTMLDivElement>;
  /** Liefert den gezogenen Platzhalter, oder null, wenn der Drop ignoriert werden soll. */
  onTokenDrop?: (e: React.DragEvent<HTMLDivElement>) => PlaceholderTemplateToken | null;
};

export type PlaceholderTemplateEditorHandle = {
  /** Platzhalter an der Cursorposition einsetzen (Ende, falls kein Cursor im Feld steht). */
  insertToken: (token: PlaceholderTemplateToken) => void;
};

/**
 * Eigene Drag-Typen: `dataTransfer.types` ist schon beim Ueberfahren lesbar (die
 * Daten selbst erst beim Drop), so erkennen die Felder vorab, ob ein Platzhalter
 * gezogen wird und ob er dort erlaubt ist.
 */
export const PLACEHOLDER_DRAG_TYPE = 'application/x-document-email-placeholder';
export const PLACEHOLDER_BODY_ONLY_DRAG_TYPE = 'application/x-document-email-placeholder-body-only';

/** Drag-Daten einheitlich setzen — `text/plain` als Fallback fuer Ziele ausserhalb des Editors. */
export function startPlaceholderDrag(
  e: React.DragEvent,
  token: PlaceholderTemplateToken,
  options?: { bodyOnly?: boolean },
): void {
  e.dataTransfer.setData('text/plain', `{{${token.name}}}`);
  e.dataTransfer.setData(PLACEHOLDER_DRAG_TYPE, token.name);
  if (options?.bodyOnly) e.dataTransfer.setData(PLACEHOLDER_BODY_ONLY_DRAG_TYPE, token.name);
  e.dataTransfer.effectAllowed = 'copy';
}

const TOKEN_SPLIT_PATTERN = /(\{\{\s*[A-Za-z0-9_äöüÄÖÜß]+\s*\}\})/g;
const TOKEN_NAME_PATTERN = /^\{\{\s*([A-Za-z0-9_äöüÄÖÜß]+)\s*\}\}$/;

const CHIP_CLASS =
  'mx-0.5 inline-block select-none rounded bg-blue-50 px-1.5 text-[13px] font-medium leading-5 text-pipedrive-blue';

/** Senkrechter Balken, der beim Ziehen die Einfuegestelle zeigt (siehe `.template-drop-caret`). */
const DROP_CARET_CLASS = 'template-drop-caret';

function createChip(token: PlaceholderTemplateToken): HTMLSpanElement {
  const chip = document.createElement('span');
  chip.dataset.placeholder = token.name;
  chip.contentEditable = 'false';
  chip.className = CHIP_CLASS;
  chip.textContent = token.label;
  return chip;
}

function renderValue(
  root: HTMLElement,
  value: string,
  resolveToken: (name: string) => PlaceholderTemplateToken | null,
): void {
  root.replaceChildren();
  for (const part of value.split(TOKEN_SPLIT_PATTERN)) {
    if (!part) continue;
    const match = part.match(TOKEN_NAME_PATTERN);
    const token = match ? resolveToken(match[1]) : null;
    root.appendChild(token ? createChip(token) : document.createTextNode(part));
  }
}

function serialize(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent ?? '';
        continue;
      }
      if (!(child instanceof HTMLElement)) continue;
      if (child.dataset.filler === 'true' || child.dataset.dropCaret === 'true') continue;
      const name = child.dataset.placeholder;
      if (name) {
        out += `{{${name}}}`;
        continue;
      }
      if (child.tagName === 'BR') {
        out += '\n';
        continue;
      }
      // Browser erzeugen beim Einfuegen mitunter Bloecke — als Zeilenumbruch lesen.
      if (out.length > 0 && !out.endsWith('\n')) out += '\n';
      walk(child);
    }
  };
  walk(root);
  return out;
}

type CaretCandidate = { container: Node; offset: number; left: number; top: number; bottom: number };

function collapsedRect(node: Node, offset: number): DOMRect | null {
  const range = document.createRange();
  try {
    range.setStart(node, offset);
    range.collapse(true);
  } catch {
    return null;
  }
  const rect = range.getBoundingClientRect();
  if (rect.height > 0) return rect;
  const rects = range.getClientRects();
  return rects.length > 0 ? rects[0] : null;
}

/**
 * Alle moeglichen Cursorpositionen mit ihrer Bildschirmlage. Bewusst selbst
 * gemessen statt via `caretPositionFromPoint`: das klammert je nach Browser auf
 * Zeilenanfang oder -ende, wodurch Marke und Einfuegestelle auseinanderlaufen.
 */
function caretCandidates(root: HTMLElement): CaretCandidate[] {
  const candidates: CaretCandidate[] = [];
  const push = (container: Node, offset: number, rect: DOMRect | null, left?: number) => {
    if (!rect || rect.height === 0) return;
    candidates.push({
      container,
      offset,
      left: left ?? rect.left,
      top: rect.top,
      bottom: rect.bottom,
    });
  };
  const walk = (node: Node) => {
    node.childNodes.forEach((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const length = (child.textContent ?? '').length;
        for (let offset = 0; offset <= length; offset += 1) {
          push(child, offset, collapsedRect(child, offset));
        }
        return;
      }
      if (!(child instanceof HTMLElement)) return;
      if (child.dataset.dropCaret === 'true') return;
      // Chips und Umbrueche sind unteilbar — davor oder dahinter.
      if (child.dataset.placeholder || child.tagName === 'BR') {
        const rect = child.getBoundingClientRect();
        push(node, index, rect, rect.left);
        push(node, index + 1, rect, rect.right);
        return;
      }
      walk(child);
    });
  };
  walk(root);
  return candidates;
}

/** Naechstgelegene Cursorposition zum Punkt — Zeile zaehlt mehr als Spalte. */
function findCaretRange(root: HTMLElement, x: number, y: number): Range | null {
  const candidates = caretCandidates(root);
  const range = document.createRange();
  if (candidates.length === 0) {
    range.selectNodeContents(root);
    range.collapse(false);
    return range;
  }
  let best = candidates[0];
  let bestScore = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const verticalGap =
      y < candidate.top ? candidate.top - y : y > candidate.bottom ? y - candidate.bottom : 0;
    const score = verticalGap * 1000 + Math.abs(candidate.left - x);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  try {
    range.setStart(best.container, best.offset);
    range.collapse(true);
  } catch {
    return null;
  }
  return range;
}

export const PlaceholderTemplateEditor = React.forwardRef<PlaceholderTemplateEditorHandle, Props>(
  function PlaceholderTemplateEditor(
    {
      value,
      onChange,
      resolveToken,
      ariaLabelledBy,
      className,
      multiline = false,
      placeholder,
      onFocus,
      onDragOver,
      onTokenDrop,
    },
    ref,
  ) {
    const editorRef = React.useRef<HTMLDivElement | null>(null);
    /** Letzter selbst gemeldeter Wert — verhindert, dass wir beim Tippen das DOM neu aufbauen. */
    const lastEmitted = React.useRef<string | null>(null);
    const dropCaretRef = React.useRef<HTMLSpanElement | null>(null);
    const lastDropPoint = React.useRef<{ x: number; y: number } | null>(null);

    React.useEffect(() => {
      const el = editorRef.current;
      if (!el || value === lastEmitted.current) return;
      renderValue(el, value, resolveToken);
      lastEmitted.current = value;
    }, [value, resolveToken]);

    const emit = () => {
      const el = editorRef.current;
      if (!el) return;
      const next = serialize(el);
      lastEmitted.current = next;
      onChange(next);
    };

    const currentRange = (): Range => {
      const el = editorRef.current;
      const selection = window.getSelection();
      if (el && selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (el.contains(range.commonAncestorContainer)) return range;
      }
      const atEnd = document.createRange();
      if (el) {
        atEnd.selectNodeContents(el);
        atEnd.collapse(false);
      }
      return atEnd;
    };

    const placeCaretAfter = (node: Node) => {
      const range = document.createRange();
      range.setStartAfter(node);
      range.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    };

    const insertNodeAtRange = (node: Node, range: Range) => {
      const el = editorRef.current;
      if (!el || !el.contains(range.commonAncestorContainer)) return;
      range.deleteContents();
      range.insertNode(node);
      // Ohne folgenden Knoten laesst sich der Cursor hinter einem Chip nicht setzen.
      if (!node.nextSibling) node.parentNode?.appendChild(document.createTextNode(''));
      placeCaretAfter(node);
      el.focus();
      emit();
    };

    React.useImperativeHandle(ref, () => ({
      insertToken: (token) => insertNodeAtRange(createChip(token), currentRange()),
    }));

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (!multiline) return;
      const br = document.createElement('br');
      const range = currentRange();
      const el = editorRef.current;
      if (!el || !el.contains(range.commonAncestorContainer)) return;
      range.deleteContents();
      range.insertNode(br);
      // Ein Umbruch am Ende wird erst mit einem Folge-`br` sichtbar; der zaehlt nicht zum Inhalt.
      if (!br.nextSibling) {
        const filler = document.createElement('br');
        filler.dataset.filler = 'true';
        br.after(filler);
      }
      placeCaretAfter(br);
      emit();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      if (!text) return;
      insertNodeAtRange(document.createTextNode(multiline ? text : text.replace(/\s+/g, ' ')), currentRange());
    };

    const hideDropCaret = () => {
      lastDropPoint.current = null;
      const caret = dropCaretRef.current;
      if (!caret) return;
      caret.remove();
      dropCaretRef.current = null;
      // Das Einhaengen der Marke splittet Textknoten — wieder zusammenfuehren.
      editorRef.current?.normalize();
    };

    /** Marke an die Stelle unter dem Zeiger setzen, damit die Einfuegeposition sichtbar ist. */
    const showDropCaret = (x: number, y: number) => {
      const el = editorRef.current;
      if (!el) return;
      // `dragover` feuert sehr oft; winzige Bewegungen aendern die Stelle nicht.
      const last = lastDropPoint.current;
      if (last && Math.abs(last.x - x) < 2 && Math.abs(last.y - y) < 2) return;
      lastDropPoint.current = { x, y };
      const range = findCaretRange(el, x, y);
      if (!range || !el.contains(range.commonAncestorContainer)) {
        hideDropCaret();
        return;
      }
      let caret = dropCaretRef.current;
      if (!caret) {
        caret = document.createElement('span');
        caret.dataset.dropCaret = 'true';
        caret.contentEditable = 'false';
        caret.className = DROP_CARET_CLASS;
        dropCaretRef.current = caret;
      }
      // Ein bereits eingehaengter Knoten wird durch `insertNode` verschoben.
      range.insertNode(caret);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      onDragOver?.(e);
      // Der Aufrufer entscheidet per `preventDefault` und `dropEffect`, ob hier
      // ueberhaupt abgelegt werden darf.
      if (!e.defaultPrevented || e.dataTransfer.dropEffect === 'none') {
        hideDropCaret();
        return;
      }
      showDropCaret(e.clientX, e.clientY);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      const el = editorRef.current;
      const next = e.relatedTarget;
      if (el && next instanceof Node && el.contains(next)) return;
      hideDropCaret();
    };

    /** Sicherheitsnetz: Abbruch oder Drop woanders beendet den Zug ohne unsere Events. */
    React.useEffect(() => {
      const onDragEnd = () => hideDropCaret();
      document.addEventListener('dragend', onDragEnd);
      return () => document.removeEventListener('dragend', onDragEnd);
    }, []);

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      const isPlaceholderDrag = e.dataTransfer.types.includes(PLACEHOLDER_DRAG_TYPE);
      const token = onTokenDrop?.(e) ?? null;
      const point = { x: e.clientX, y: e.clientY };
      // Marke zuerst entfernen, damit die Stelle ohne sie gemessen wird.
      hideDropCaret();
      if (!token) {
        // Abgelehnter Platzhalter: ohne `preventDefault` wuerde der Browser den
        // `text/plain`-Inhalt als normalen Text ins Feld schreiben.
        if (isPlaceholderDrag) e.preventDefault();
        return;
      }
      e.preventDefault();
      const el = editorRef.current;
      const range = (el ? findCaretRange(el, point.x, point.y) : null) ?? currentRange();
      insertNodeAtRange(createChip(token), range);
    };

    return (
      <div className="relative">
        {value.length === 0 && placeholder ? (
          <span className="pointer-events-none absolute left-3 top-2 text-sm text-slate-400">
            {placeholder}
          </span>
        ) : null}
        <div
          ref={editorRef}
          role="textbox"
          tabIndex={0}
          contentEditable
          suppressContentEditableWarning
          aria-labelledby={ariaLabelledBy}
          aria-multiline={multiline}
          onInput={emit}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={onFocus}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`${
            multiline
              ? 'whitespace-pre-wrap break-words'
              : // `overflow-y-hidden` explizit: sonst macht CSS aus dem sichtbaren
                // Gegenstueck zu `overflow-x-auto` ebenfalls `auto` — mit nutzloser
                // senkrechter Scrollleiste.
                'whitespace-pre overflow-x-auto overflow-y-hidden'
          } ${className ?? ''}`}
        />
      </div>
    );
  },
);
